import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { ImageData } from "./ImageModal";
import { useStableUrls } from "../hooks/useStableUrl";
import { usePinchZoom } from "../hooks/usePinchZoom";
import { useI18n } from "../i18n";

interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceImage: ImageData | null;
}

export default function EditModal({ isOpen, onClose, sourceImage }: EditModalProps) {
  const { t } = useI18n();
  const [editInstruction, setEditInstruction] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // latestImageId is the most recent in the chain - used for querying full history
  const [latestImageId, setLatestImageId] = useState(sourceImage?._id);
  // selectedImageId is the currently viewed/highlighted image in history
  const [selectedImageId, setSelectedImageId] = useState(sourceImage?._id);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const zoom = usePinchZoom();

  const createEdit = useMutation(api.images.createEditPending);
  const removeImage = useMutation(api.images.remove);
  const rawEditHistory = useQuery(
    api.images.getEditHistory,
    latestImageId ? { imageId: latestImageId } : "skip"
  );
  // Stabilize URLs to prevent constant image reloading
  const editHistory = useStableUrls(rawEditHistory);

  // Reset state when modal opens with new image
  useEffect(() => {
    if (sourceImage) {
      setLatestImageId(sourceImage._id);
      setSelectedImageId(sourceImage._id);
      setEditInstruction("");
    }
  }, [sourceImage?._id]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (showFullscreen) {
        setShowFullscreen(false);
      } else {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, showFullscreen]);

  if (!isOpen || !sourceImage) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editInstruction.trim() || isSubmitting || !latestImageId) return;

    setIsSubmitting(true);
    try {
      const newImageId = await createEdit({
        sourceImageId: latestImageId,
        editInstruction: editInstruction.trim(),
      });
      // Update both to the new image
      setLatestImageId(newImageId);
      setSelectedImageId(newImageId);
      setEditInstruction("");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get the selected image to display
  const selectedImage = editHistory?.find(img => img._id === selectedImageId) || editHistory?.[editHistory.length - 1];
  // Check if the latest image is still generating
  const latestImage = editHistory?.[editHistory.length - 1];
  const isGenerating = latestImage?.status === "pending" || latestImage?.status === "generating";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full h-full md:w-[95vw] md:h-[90vh] md:max-w-7xl bg-white md:rounded-softer shadow-2xl flex flex-col md:flex-row overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 md:top-4 md:right-4 z-10 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center text-charcoal hover:bg-coral-400 hover:text-white transition-colors shadow-soft"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Image section - top on mobile, left on desktop */}
        <div className="flex-1 bg-linen-100 flex items-center justify-center p-4 md:p-8 min-h-[40vh] md:min-h-0 relative">
          {isGenerating && (
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10">
              <div className="bg-white rounded-soft p-6 flex flex-col items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 border-4 border-coral-400/30 rounded-full" />
                  <div className="absolute top-0 left-0 w-10 h-10 border-4 border-coral-500 rounded-full border-t-transparent animate-spin" />
                </div>
                <span className="text-charcoal font-medium">{t.editModal.applyingEdit}</span>
              </div>
            </div>
          )}
          {selectedImage?.url ? (
            <img
              src={selectedImage.url}
              alt={selectedImage.editInstruction || selectedImage.prompt}
              onClick={() => setShowFullscreen(true)}
              className="max-w-full max-h-full object-contain rounded-soft shadow-card cursor-pointer hover:opacity-90 transition-opacity"
            />
          ) : sourceImage.url ? (
            <img
              src={sourceImage.url}
              alt={sourceImage.prompt}
              onClick={() => setShowFullscreen(true)}
              className="max-w-full max-h-full object-contain rounded-soft shadow-card cursor-pointer hover:opacity-90 transition-opacity"
            />
          ) : (
            <div className="text-charcoal-muted">{t.common.noImageAvailable}</div>
          )}
        </div>

        {/* Edit panel - bottom on mobile, right on desktop */}
        <div className="w-full md:w-[340px] lg:w-[380px] bg-white border-t md:border-t-0 md:border-l border-linen-200 p-4 md:p-6 overflow-y-auto flex flex-col max-h-[55vh] md:max-h-none">
          {/* Header with delete button */}
          <div className="flex items-center justify-between mb-4 md:mb-6 mr-10">
            <h2 className="text-xl md:text-2xl font-serif text-charcoal">{t.editModal.title}</h2>
            {/* Delete button */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="group relative w-8 h-8 bg-red-50 text-red-500 rounded-lg flex items-center justify-center hover:bg-red-500 hover:text-white transition-all duration-200 hover:shadow-md"
              aria-label={t.common.delete}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              {/* Tooltip */}
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 text-xs font-medium text-white bg-charcoal rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-20">
                {t.common.delete}
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-charcoal"></span>
              </span>
            </button>
          </div>

          {/* Edit history */}
          {editHistory && editHistory.length > 0 && (
            <div className="mb-4 md:mb-6">
              <h3 className="text-xs md:text-sm font-medium text-charcoal-muted uppercase tracking-wide mb-2 md:mb-3">
                {t.editModal.editHistory}
              </h3>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {editHistory.map((historyItem, index) => (
                  <button
                    key={historyItem._id}
                    onClick={() => setSelectedImageId(historyItem._id)}
                    className={`relative flex-shrink-0 w-16 h-16 rounded-soft overflow-hidden border-2 transition-colors ${
                      selectedImageId === historyItem._id
                        ? "border-coral-500"
                        : "border-linen-200 hover:border-linen-300"
                    }`}
                  >
                    {historyItem.url ? (
                      <img
                        src={historyItem.url}
                        alt={historyItem.editInstruction || historyItem.prompt}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-linen-100 flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-charcoal-muted/30 rounded-full border-t-transparent animate-spin" />
                      </div>
                    )}
                    {index === 0 && (
                      <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] text-center py-0.5">
                        {t.common.original}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Current edit instruction (if viewing an edit) */}
          {selectedImage?.editInstruction && (
            <div className="mb-4 md:mb-6">
              <h3 className="text-xs md:text-sm font-medium text-charcoal-muted uppercase tracking-wide mb-2">
                {t.editModal.appliedEdit}
              </h3>
              <p className="text-charcoal bg-linen-50 p-3 rounded-soft text-sm leading-relaxed">
                {selectedImage.editInstruction}
              </p>
            </div>
          )}

          {/* Original prompt */}
          <div className="mb-4 md:mb-6">
            <h3 className="text-xs md:text-sm font-medium text-charcoal-muted uppercase tracking-wide mb-2">
              {t.editModal.originalPrompt}
            </h3>
            <p className="text-charcoal bg-linen-50 p-3 rounded-soft text-sm leading-relaxed line-clamp-3">
              {sourceImage.prompt}
            </p>
          </div>

          {/* Edit form */}
          <form onSubmit={handleSubmit} className="mt-auto">
            <h3 className="text-xs md:text-sm font-medium text-charcoal-muted uppercase tracking-wide mb-2">
              {t.editModal.describeYourEdit}
            </h3>
            <textarea
              value={editInstruction}
              onChange={(e) => setEditInstruction(e.target.value)}
              placeholder={t.editModal.editPlaceholder}
              rows={3}
              disabled={isSubmitting || isGenerating}
              className="w-full px-4 py-3 border border-linen-200 rounded-soft focus:outline-none focus:ring-2 focus:ring-coral-400/50 focus:border-coral-400 resize-none text-charcoal placeholder-charcoal-muted/50 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <button
              type="submit"
              disabled={!editInstruction.trim() || isSubmitting || isGenerating}
              className="w-full mt-3 py-3 px-4 bg-coral-500 text-white font-medium rounded-soft hover:bg-coral-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting || isGenerating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 rounded-full border-t-white animate-spin" />
                  {t.editModal.applyingEdit}
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  {t.editModal.applyEdit}
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Fullscreen Image Overlay */}
      {showFullscreen && (selectedImage?.url || sourceImage.url) && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 overflow-hidden"
          onClick={(e) => {
            e.stopPropagation();
            zoom.reset();
            setShowFullscreen(false);
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              zoom.reset();
              setShowFullscreen(false);
            }}
            className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center text-charcoal hover:bg-coral-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          {/* Zoom hint */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-xs md:hidden">
            {t.common.zoomHint}
          </div>
          <img
            src={selectedImage?.url || sourceImage.url || ""}
            alt={selectedImage?.editInstruction || selectedImage?.prompt || sourceImage.prompt}
            className="max-w-[90vw] max-h-[90vh] object-contain select-none"
            style={zoom.style}
            onClick={(e) => e.stopPropagation()}
            {...zoom.handlers}
          />
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50"
          onClick={(e) => {
            e.stopPropagation();
            setShowDeleteConfirm(false);
          }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium text-charcoal mb-2">{t.common.deleteImageTitle}</h3>
            <p className="text-sm text-charcoal-muted mb-4">
              {editHistory && editHistory.length > 1 && selectedImageId !== editHistory[0]?._id
                ? t.common.deleteVersionMessage
                : t.common.deletePermanentMessage}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-charcoal hover:bg-linen-100 rounded-lg transition-colors"
              >
                {t.common.cancel}
              </button>
              <button
                onClick={async () => {
                  const idToDelete = selectedImageId || sourceImage?._id;
                  if (!idToDelete) return;
                  await removeImage({ imageId: idToDelete });
                  setShowDeleteConfirm(false);
                  // If we deleted the root or only image, close modal
                  if (!editHistory || editHistory.length <= 1 || selectedImageId === editHistory[0]?._id) {
                    onClose();
                  } else {
                    // Switch to another image in history
                    const remaining = editHistory.filter(h => h._id !== idToDelete);
                    const lastRemaining = remaining[remaining.length - 1];
                    if (lastRemaining) {
                      setSelectedImageId(lastRemaining._id);
                      setLatestImageId(lastRemaining._id);
                    } else {
                      onClose();
                    }
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
              >
                {t.common.delete}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
